<!-- src/components/DocumentScanner.vue -->
<template>
  <div class="scanner-container">
    <h1>Vue.js Document Scanner</h1>

    <!-- Loading and Error Messages -->
    <div
v-if="isLoading"
class="status-message"
>
Loading OpenCV.js, please wait...
</div>
    <div
v-if="errorMessage"
class="status-message error"
>
{{ errorMessage }}
</div>

    <!-- Main Scanner UI -->
    <div v-show="!isLoading && !errorMessage">
      <div class="camera-container">
        <video
ref="video"
class="camera-feed"
autoplay
playsinline
/>
        <canvas
ref="canvas"
class="hidden-canvas"
/>
      </div>
      <div class="controls">
        <button
:disabled="!isCameraStarted"
@click="capturePage"
>
          <span class="icon">📷</span> Capture Page
        </button>
        <button
:disabled="scannedImages.length === 0"
@click="generatePdf"
>
          <span class="icon">📄</span> Generate PDF ({{ scannedImages.length }} pages)
        </button>
      </div>
    </div>

    <!-- Scanned Pages Gallery -->
    <div v-if="scannedImages.length > 0">
      <h2>Scanned Pages</h2>
      <div class="gallery">
        <div
v-for="(image, index) in scannedImages"
:key="index"
class="thumbnail"
>
          <img
:src="image"
alt="Scanned page"
>
        </div>
      </div>
    </div>
  </div>
</template>


<script>
import jsPDF from 'jspdf';
// --- FIX 1: Use a namespace import to get the entire module ---
import * as cv from '@techstark/opencv-js';

// This promise prevents the heavy initialization from running more than once.
let openCvPromise = null;

export default {
  name: 'Scanny',
  data() {
    return {
      // We will store the initialized cv object itself here after it loads
      cv: null, 
      isLoading: true,
      isCameraStarted: false,
      errorMessage: '',
      scannedImages: [],
      videoStream: null,
    };
  },
  mounted() {
    this.initializeScanner();
  },
  beforeDestroy() {
    this.stopCamera();
  },
  methods: {
    /**
     * Loads the opencv.js module using the correct export.
     */
    loadOpenCv() {
      if (!openCvPromise) {
        // --- FIX 2: Call the 'default' function from the imported module object ---
        openCvPromise = cv.default({
          locateFile: (path, scriptDirectory) => {
            // Path MUST correspond to the 'to:' path in your vue.config.js
            return `/wasm/${path}`;
          },
        });
      }
      return openCvPromise;
    },

    async initializeScanner() {
      try {
        console.log("Loading OpenCV from @techstark/opencv-js...");
        // The resolved promise IS the fully initialized cv object.
        this.cv = await this.loadOpenCv();
        console.log("OpenCV is ready.");
        console.log(this.cv.getBuildInformation()); // Verification step

        this.$nextTick(() => {
          this.isLoading = false;
          this.startCamera();
        });

      } catch (error) {
        this.isLoading = false;
        this.errorMessage = `Failed to load OpenCV module. Error: ${error.message}`;
        console.error(error);
      }
    },
    
    // --- NO OTHER CHANGES ARE NEEDED BELOW THIS LINE ---

    async startCamera() {
      if (!this.$refs.video) {
        this.errorMessage = "Error: The video element reference was not found.";
        return;
      }
      this.errorMessage = '';
      const constraints = { video: { width: { ideal: 1280 }, height: { ideal: 720 }, facingMode: 'environment' } };

      try {
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        this.$refs.video.srcObject = stream;
        this.videoStream = stream;
        this.isCameraStarted = true;
      } catch (err) {
        console.error("Camera access error:", err);
        this.errorMessage = `Camera Error: ${err.name}. Please grant permission and ensure you are on a secure (HTTPS) connection.`;
        this.isCameraStarted = false;
      }
    },

    stopCamera() {
      if (this.videoStream) {
        this.videoStream.getTracks().forEach(track => track.stop());
        this.isCameraStarted = false;
      }
    },
    
    capturePage() {
      if (!this.cv) { this.errorMessage = "OpenCV is not ready."; return; }
      const cv = this.cv;
      const video = this.$refs.video;
      const canvas = this.$refs.canvas;
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const context = canvas.getContext('2d');
      context.drawImage(video, 0, 0, canvas.width, canvas.height);

      let src, gray, contours, hierarchy;
      try {
        src = cv.imread(canvas);
        gray = new cv.Mat();
        cv.cvtColor(src, gray, cv.COLOR_RGBA2GRAY);
        cv.GaussianBlur(gray, gray, new cv.Size(5, 5), 0);
        cv.Canny(gray, gray, 75, 200);

        contours = new cv.MatVector();
        hierarchy = new cv.Mat();
        cv.findContours(gray, contours, hierarchy, cv.RETR_CCOMP, cv.CHAIN_APPROX_SIMPLE);
        
        let maxArea = 0, bestContour = null;
        for (let i = 0; i < contours.size(); ++i) {
            let cnt = contours.get(i);
            let area = cv.contourArea(cnt);
            let peri = cv.arcLength(cnt, true);
            let approx = new cv.Mat();
            cv.approxPolyDP(cnt, approx, 0.02 * peri, true);

            if (area > 10000 && approx.rows === 4) {
                if (area > maxArea) {
                    maxArea = area;
                    bestContour = approx.clone();
                }
            }
            cnt.delete();
            approx.delete();
        }

        if (bestContour) {
           const imageUrl = canvas.toDataURL('image/jpeg');
           this.scannedImages.push(imageUrl);
           bestContour.delete();
        } else {
            alert("No document outline detected. Please ensure the document is on a contrasting background with good lighting.");
        }
      } catch (error) {
          console.error("Error during OpenCV processing:", error);
          this.errorMessage = "An error occurred during image processing.";
      } finally {
        if (src) src.delete();
        if (gray) gray.delete();
        if (contours) contours.delete();
        if (hierarchy) hierarchy.delete();
      }
    },
    
    generatePdf() {
      if (this.scannedImages.length === 0) return;
      
      const doc = new jsPDF();
      this.scannedImages.forEach((imageUrl, index) => {
        if (index > 0) doc.addPage();
        const imgProps = doc.getImageProperties(imageUrl);
        const pdfWidth = doc.internal.pageSize.getWidth();
        const pdfHeight = (imgProps.height * pdfWidth) / imgProps.width;
        doc.addImage(imageUrl, 'JPEG', 0, 0, pdfWidth, pdfHeight);
      });
      doc.save('scanned-document.pdf');
    },
  },
};
</script>

<style scoped>
.scanner-container {
  max-width: 800px;
  margin: 20px auto;
  padding: 20px;
  box-shadow: 0 4px 8px rgba(0,0,0,0.1);
  border-radius: 8px;
  font-family: Arial, sans-serif;
  text-align: center;
}
.camera-container {
  position: relative;
  width: 100%;
  background: #2c3e50;
  border-radius: 8px;
  overflow: hidden;
}
.camera-feed {
  width: 100%;
  height: auto;
  display: block;
}
.hidden-canvas {
  display: none;
}
.status-message {
  padding: 15px;
  border-radius: 5px;
  background-color: #eaf4ff;
  color: #0056b3;
  margin-bottom: 20px;
}
.status-message.error {
  background-color: #ffebee;
  color: #c62828;
}
.controls {
  margin-top: 20px;
  display: flex;
  justify-content: center;
  gap: 15px;
}
button {
  padding: 12px 25px;
  font-size: 16px;
  cursor: pointer;
  background-color: #3498db;
  color: white;
  border: none;
  border-radius: 5px;
  transition: background-color 0.3s;
}
button:disabled {
  background-color: #bdc3c7;
  cursor: not-allowed;
}
button:hover:not(:disabled) {
  background-color: #2980b9;
}
.icon {
  margin-right: 8px;
}
.gallery {
  margin-top: 20px;
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: center;
}
.thumbnail img {
  width: 120px;
  height: auto;
  border: 2px solid #ddd;
  border-radius: 4px;
}
</style>